import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";

type CarouselProps = {
  /**
   * Carousel items. Each item should carry its own width (e.g. `w-72 shrink-0`)
   * and `snap-start` so it snaps into place — Carousel only owns the scroll
   * track and the prev/next controls, not the item shape.
   */
  children: ReactNode;
  className?: string;
  /**
   * Makes the carousel an infinite loop (it can keep going in the same
   * direction doing full circles — the items are rendered twice and the
   * scroll position is silently reset each time a full cycle is crossed)
   * and auto-advances one item every few seconds. Pauses while the user
   * hovers or touches the track, and does nothing when the items already
   * fit on screen.
   */
  autoRoll?: boolean;
  /**
   * Shift the track left by half a card so both edges show a partial card
   * (the landing-page look). Turn off to give the carousel a clear start:
   * the first item sits flush with the left edge and only the right edge
   * cuts a card when the content overflows.
   */
  peek?: boolean;
};

const AUTO_ROLL_INTERVAL_MS = 4000;
/** Must match the track's `gap-6`. */
const GAP_PX = 24;

const ARROW_BUTTON_CLASSES =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-ink text-ink transition-colors hover:bg-ink hover:text-white";

/** Width of one full set of items (the loop cycle) including the gap that
 *  separates it from its duplicate. */
function cycleWidth(track: HTMLDivElement) {
  return (track.scrollWidth + GAP_PX) / 2;
}

/**
 * Reusable horizontal scroll-snap carousel (no external dependency). Used for
 * both the course-type and the house carousels on the landing page.
 */
export default function Carousel({
  children,
  className = "",
  autoRoll = false,
  peek = true,
}: CarouselProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const dragRef = useRef<{
    startX: number;
    startScroll: number;
    dragging: boolean;
  } | null>(null);
  const [overflows, setOverflows] = useState(false);

  const looping = autoRoll && overflows;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const track = trackRef.current;
    if (!wrapper || !track) return;

    const update = () => {
      // When the items are duplicated for the infinite loop, the real
      // content width is one set, not the doubled scrollWidth.
      const setWidth =
        track.dataset.duplicated === "true"
          ? (track.scrollWidth - GAP_PX) / 2
          : track.scrollWidth;
      const item = track.querySelector<HTMLElement>(":scope > *");

      // Measured against the clipping wrapper, not the track itself — the
      // track's own width grows once the peek margin below is applied, so
      // using its clientWidth here would keep pushing the threshold out.
      const nowOverflows = setWidth > wrapper.clientWidth + 1;
      setOverflows(nowOverflows);

      // The right edge naturally cuts the last visible card short (the
      // items don't divide evenly into the viewport). Mirror that on the
      // left with a static layout offset: shift the track left by half a
      // card via negative margin. Width is `auto`, so the browser grows
      // the track by the same amount to keep its right edge flush with the
      // wrapper — only the left overhang gets clipped. (A scrollLeft-based
      // peek doesn't survive resizing — scroll-snap silently resets it back
      // to 0. `transform` avoids that fight too but leaves a gap on the
      // right instead, since it doesn't grow the box to compensate.)
      track.style.marginLeft =
        peek && nowOverflows && item ? `-${item.offsetWidth / 2}px` : "";
    };
    update();

    const observer = new ResizeObserver(update);
    observer.observe(wrapper);

    // Mouse drag-to-scroll (touch already pans natively). Snap and smooth
    // behavior are suspended during the drag — both fight direct scrollLeft
    // writes — and restored on release, which also re-snaps to the nearest
    // item. The drag (and its pointer capture) only starts after a small
    // movement threshold: capturing right on pointerdown would swallow the
    // click, making links/buttons inside the cards unclickable.
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      dragRef.current = {
        startX: e.clientX,
        startScroll: track.scrollLeft,
        dragging: false,
      };
    };
    const onPointerMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;

      if (!drag.dragging) {
        if (Math.abs(e.clientX - drag.startX) < 5) return;
        drag.dragging = true;
        pausedRef.current = true;
        track.style.scrollBehavior = "auto";
        track.style.scrollSnapType = "none";
        track.setPointerCapture(e.pointerId);
      }

      let next = drag.startScroll - (e.clientX - drag.startX);
      // Infinite mode: dragging backwards past the start jumps one cycle
      // forward (invisible, the duplicate set is identical) so the drag can
      // keep going. Forward wrapping is handled by the scroll listener.
      if (track.dataset.duplicated === "true" && next <= 0) {
        const cycle = cycleWidth(track);
        drag.startScroll += cycle;
        next += cycle;
      }
      track.scrollLeft = next;
    };
    const endDrag = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;
      if (!drag.dragging) return;
      track.style.scrollBehavior = "";
      track.style.scrollSnapType = "";
      pausedRef.current = track.matches(":hover");
      if (track.hasPointerCapture(e.pointerId)) {
        track.releasePointerCapture(e.pointerId);
      }
    };
    // The cards' images would otherwise start a native HTML drag.
    const onDragStart = (e: Event) => e.preventDefault();
    track.addEventListener("pointerdown", onPointerDown);
    track.addEventListener("pointermove", onPointerMove);
    track.addEventListener("pointerup", endDrag);
    track.addEventListener("pointercancel", endDrag);
    track.addEventListener("dragstart", onDragStart);

    return () => {
      observer.disconnect();
      track.removeEventListener("pointerdown", onPointerDown);
      track.removeEventListener("pointermove", onPointerMove);
      track.removeEventListener("pointerup", endDrag);
      track.removeEventListener("pointercancel", endDrag);
      track.removeEventListener("dragstart", onDragStart);
    };
  }, [peek]);

  function scroll(direction: 1 | -1) {
    const track = trackRef.current;
    if (!track) return;

    const item = track.querySelector<HTMLElement>(":scope > *");
    const amount = item ? item.offsetWidth + GAP_PX : track.clientWidth * 0.8;

    // Going backwards from the first set: jump one cycle forward (invisible,
    // the duplicate looks identical) so there is always room to scroll back.
    if (looping && direction === -1 && track.scrollLeft < amount) {
      track.scrollTo({
        left: track.scrollLeft + cycleWidth(track),
        behavior: "instant",
      });
    }

    track.scrollBy({ left: amount * direction, behavior: "smooth" });
  }

  useEffect(() => {
    const track = trackRef.current;
    if (!looping || !track) return;

    // Seamless wrap: once a full cycle has scrolled past, snap back by one
    // cycle — the duplicated set makes the jump invisible.
    const onScroll = () => {
      const cycle = cycleWidth(track);
      if (track.scrollLeft >= cycle) {
        track.scrollTo({
          left: track.scrollLeft - cycle,
          behavior: "instant",
        });
        // Keep an in-flight drag's reference point in sync with the jump.
        if (dragRef.current) dragRef.current.startScroll -= cycle;
      }
    };
    track.addEventListener("scroll", onScroll);

    const pause = () => {
      pausedRef.current = true;
    };
    const resume = () => {
      if (!dragRef.current) pausedRef.current = false;
    };
    track.addEventListener("mouseenter", pause);
    track.addEventListener("mouseleave", resume);
    track.addEventListener("touchstart", pause);
    track.addEventListener("touchend", resume);

    const id = setInterval(() => {
      if (pausedRef.current) return;

      const item = track.querySelector<HTMLElement>(":scope > *");
      const amount = item ? item.offsetWidth + GAP_PX : track.clientWidth * 0.8;
      track.scrollBy({ left: amount, behavior: "smooth" });
    }, AUTO_ROLL_INTERVAL_MS);

    return () => {
      clearInterval(id);
      track.removeEventListener("scroll", onScroll);
      track.removeEventListener("mouseenter", pause);
      track.removeEventListener("mouseleave", resume);
      track.removeEventListener("touchstart", pause);
      track.removeEventListener("touchend", resume);
    };
  }, [looping]);

  return (
    <div className={className}>
      <div ref={wrapperRef} className="overflow-hidden">
        <div
          ref={trackRef}
          data-duplicated={looping ? "true" : "false"}
          className={`flex cursor-grab snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-2 select-none active:cursor-grabbing [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${overflows ? "" : "justify-center"}`}
        >
          <Fragment key="original">{children}</Fragment>
          {looping && (
            <Fragment key="duplicate">
              <span aria-hidden className="contents">
                {children}
              </span>
            </Fragment>
          )}
        </div>
      </div>

      <div
        className={`mt-4 flex justify-end gap-3 px-6 sm:px-10 ${overflows ? "" : "hidden"}`}
      >
        <button
          type="button"
          onClick={() => scroll(-1)}
          aria-label="Anterior"
          className={ARROW_BUTTON_CLASSES}
        >
          <ArrowIcon className="h-4 w-4 rotate-180" />
        </button>
        <button
          type="button"
          onClick={() => scroll(1)}
          aria-label="Siguiente"
          className={ARROW_BUTTON_CLASSES}
        >
          <ArrowIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m9 5 7 7-7 7" />
    </svg>
  );
}
